import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import type { projectionHorizonOptions } from '@clair-avenir/shared';
import { Button } from '@ds/components/core/Button';
import { Card, CardBody, CardHeader } from '@ds/components/core/Card';
import { AuthenticatedShell, Page, SectionHead } from '@/components/AppShell';
import { EmptyState } from '@/components/EmptyState';
import { useProjectionMonths } from '@/features/dashboard/api';
import { ProjectionChart } from '@/features/projections/components/ProjectionChart';
import { ProjectionTable } from '@/features/projections/components/ProjectionTable';
import { formatBRL } from '@/lib/format';

export const Route = createFileRoute('/_authenticated/projecoes')({ component: ProjectionsPage });

type Horizon = (typeof projectionHorizonOptions)[number];
const HORIZONS: { value: Horizon; label: string }[] = [
  { value: '6', label: '6 meses' },
  { value: '12', label: '12 meses' },
  { value: '24', label: '24 meses' },
  { value: 'max', label: 'Até a última parcela' },
];

function ProjectionsPage() {
  const [horizon, setHorizon] = useState<Horizon>('12');
  const { data, isLoading, isError, refetch } = useProjectionMonths(horizon);
  const hasNothing = data && data.currentBalanceCents === 0 && data.months.every((m) => m.items.length === 0 && m.balanceCents === 0);

  return (
    <AuthenticatedShell title="Projeções">
      <Page>
        <div data-testid="projections-page">
          <SectionHead
            title="Saldo mês a mês"
            action={
              <div role="group" aria-label="Horizonte da projeção" style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {HORIZONS.map((h) => (
                  <Button key={h.value} size="sm" variant={horizon === h.value ? 'secondary' : 'ghost'} aria-pressed={horizon === h.value} data-testid={`projections-horizon-${h.value}`} onClick={() => setHorizon(h.value)}>
                    {h.label}
                  </Button>
                ))}
              </div>
            }
          />

          {isError ? (
            <EmptyState
              testId="projections-error"
              icon="triangle-alert"
              title="Não foi possível carregar as projeções"
              description="Tente de novo em instantes."
              action={
                <Button size="sm" variant="secondary" data-testid="projections-retry" onClick={() => void refetch()}>
                  Tentar de novo
                </Button>
              }
            />
          ) : isLoading || !data ? (
            <p style={{ font: 'var(--type-body)', color: 'var(--text-muted)', margin: 0 }}>Carregando...</p>
          ) : hasNothing ? (
            <EmptyState testId="projections-empty" icon="trending-up" title="Nada pra projetar ainda" description="Cadastre uma conta com saldo, fixos ou parcelas pra ver como o saldo evolui mês a mês." />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--gutter-section)' }}>
              <Card>
                <CardHeader title="Saldo ao fim de cada mês" description="Parte do que você tem nas contas hoje e soma tudo que entra e sai em cada mês." />
                <CardBody>
                  <div data-testid="projections-accounts" style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 20px', alignItems: 'baseline', marginBottom: 12, font: 'var(--type-body)', color: 'var(--text-body)' }}>
                    <span style={{ font: 'var(--type-label)', color: 'var(--text-primary)' }}>
                      Nas contas hoje: <span data-testid="projections-current-balance" style={{ fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums' }}>{formatBRL(data.currentBalanceCents)}</span>
                    </span>
                    {data.accounts.map((a) => (
                      <span key={a.accountId} data-testid={`projections-account-${a.accountId}`} style={{ color: 'var(--text-muted)' }}>
                        {a.nickname}: <span style={{ fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums' }}>{formatBRL(a.balanceCents)}</span>
                      </span>
                    ))}
                  </div>
                  <ProjectionChart months={data.months} currentBalanceCents={data.currentBalanceCents} />
                </CardBody>
              </Card>
              <Card>
                <CardHeader title="Mês a mês" description="Receba e pague tudo do mês: fixos, parcelas, faturas de cartão e lançamentos futuros. Clique num mês pra ver os itens." />
                <CardBody>
                  {data.truncated ? (
                    <p role="status" data-testid="projections-truncated" style={{ font: 'var(--type-caption)', color: 'var(--text-muted)', margin: '0 0 8px' }}>
                      Mostrando os primeiros 420 meses.
                    </p>
                  ) : null}
                  <ProjectionTable months={data.months} />
                </CardBody>
              </Card>
            </div>
          )}
        </div>
      </Page>
    </AuthenticatedShell>
  );
}
